import React, { useEffect, useState } from "react";
import { Cookie, Globe, ChevronDown, Check, Settings2, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const LANG_OPTIONS = [
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "da", label: "Dansk", flag: "🇩🇰" },
];

export default function CookieBanner() {
  const { lang, setLang, t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showLangs, setShowLangs] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const consent = localStorage.getItem("puri_cookie_consent");
      if (!consent) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const acceptAll = () => {
    try {
      localStorage.setItem("puri_cookie_consent", JSON.stringify({
        necessary: true, analytics: true, marketing: true, date: new Date().toISOString()
      }));
    } catch {}
    setVisible(false);
  };

  const savePrefs = () => {
    try {
      localStorage.setItem("puri_cookie_consent", JSON.stringify({
        ...prefs, date: new Date().toISOString()
      }));
    } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  const currentLang = LANG_OPTIONS.find((l) => l.code === lang) || LANG_OPTIONS[0];

  return (
    <div className="fixed inset-x-0 bottom-0 z-[200] p-3 md:p-6 pointer-events-none">
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div className="bg-[#FFF7EA] border-2 border-[#D4A94A] rounded-2xl shadow-2xl overflow-hidden animate-[slideUp_0.5s_ease-out]">
          {/* Header strip */}
          <div className="bg-gradient-to-r from-[#7A1F1F] to-[#5A1414] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#E9C97A]">
              <Cookie size={20} />
              <span className="font-display tracking-widest text-sm md:text-base font-bold">{t.cookie.title}</span>
            </div>
            {/* Language selector */}
            <div className="relative">
              <button
                onClick={() => setShowLangs((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1B1512]/40 border border-[#D4A94A]/40 text-[#FBEFD9] hover:border-[#D4A94A] transition-colors text-xs md:text-sm"
                aria-label={t.cookie.language}
              >
                <Globe size={14} />
                <span className="hidden sm:inline">{currentLang.flag}</span>
                <span>{currentLang.label}</span>
                <ChevronDown size={14} className={`transition-transform ${showLangs ? "rotate-180" : ""}`} />
              </button>
              {showLangs && (
                <div className="absolute right-0 mt-2 w-44 bg-[#1B1512] border border-[#D4A94A]/40 rounded-lg shadow-2xl overflow-hidden z-10">
                  {LANG_OPTIONS.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => { setLang(opt.code); setShowLangs(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-[#7A1F1F]/60 transition-colors ${
                        lang === opt.code ? "bg-[#7A1F1F]/40 text-[#E9C97A]" : "text-[#FBEFD9]"
                      }`}
                    >
                      <span className="text-base">{opt.flag}</span>
                      <span className="flex-1">{opt.label}</span>
                      {lang === opt.code && <Check size={14} className="text-[#E9C97A]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="p-5 md:p-6">
            <p className="text-[#1B1512]/85 text-sm md:text-base leading-relaxed">
              {t.cookie.message}
            </p>

            {expanded && (
              <div className="mt-5 space-y-3 border-t border-[#D4A94A]/30 pt-4">
                <PrefRow
                  label={t.cookie.necessary}
                  desc={t.cookie.necessaryDesc}
                  checked={true}
                  disabled
                  onChange={() => {}}
                />
                <PrefRow
                  label={t.cookie.analytics}
                  desc={t.cookie.analyticsDesc}
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                />
                <PrefRow
                  label={t.cookie.marketing}
                  desc={t.cookie.marketingDesc}
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                />
              </div>
            )}

            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              {!expanded ? (
                <>
                  <button
                    onClick={acceptAll}
                    className="btn-primary flex-1 justify-center"
                  >
                    <Check size={16} /> {t.cookie.accept}
                  </button>
                  <button
                    onClick={() => setExpanded(true)}
                    className="btn-outline flex-1 justify-center"
                  >
                    <Settings2 size={16} /> {t.cookie.customize}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={savePrefs} className="btn-primary flex-1 justify-center">
                    <Check size={16} /> {t.cookie.save}
                  </button>
                  <button onClick={acceptAll} className="btn-outline flex-1 justify-center">
                    {t.cookie.accept}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function PrefRow({ label, desc, checked, onChange, disabled }) {
  return (
    <div className="flex items-start gap-3">
      <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`mt-0.5 w-10 h-6 rounded-full flex-shrink-0 transition-colors relative ${
          checked ? "bg-[#E86D1F]" : "bg-[#D4A94A]/40"
        } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all shadow ${
            checked ? "left-[18px]" : "left-0.5"
          }`}
        />
      </button>
      <div className="flex-1">
        <p className="font-semibold text-[#7A1F1F] text-sm">{label}</p>
        <p className="text-xs text-[#1B1512]/70 mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
